// Step-up authentication. Sensitive routes answer 401 `reauth_required`; withReauth() asks the
// merchant to confirm it's them (password, or a code sent by email), calls POST /api/auth/reauth,
// then retries the original call once. <ReauthHost/> is mounted by the shells.
import { useEffect, useRef, useState } from 'react'
import { KeyRound, Mail } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Dialog } from './dialog'
import { Button } from './button'
import { Field } from './field'
import { Input } from './input'
import { errorMessage } from './feedback'

type Request = { resolve: () => void; reject: (e: unknown) => void }
let open: ((r: Request) => void) | null = null
let inflight: Promise<void> | null = null

export const isReauthRequired = (e: unknown) => e instanceof ApiError && e.status === 401 && e.code === 'reauth_required'

/** Ask for step-up confirmation now (resolves when confirmed, rejects `reauth_cancelled`). Concurrent callers share one dialog. */
export function requestReauth(): Promise<void> {
  if (inflight) return inflight
  if (!open) return Promise.reject(new ApiError(401, 'reauth_required', 'Please confirm your password and try again.'))
  const p: Promise<void> = new Promise<void>((resolve, reject) => open!({ resolve, reject })).finally(() => { if (inflight === p) inflight = null })
  inflight = p
  return p
}

const cancelled = () => new ApiError(401, 'reauth_cancelled', 'Confirmation cancelled.')

/**
 * Run a sensitive call; if the server wants a fresh login it asks the merchant and retries once:
 * `await withReauth(() => api.put('/api/admin/payments/stripe', body))`.
 * useForm() and useMutation callers can wrap their mutationFn with it.
 */
export async function withReauth<T>(fn: () => Promise<T>): Promise<T> {
  try { return await fn() }
  catch (e) {
    if (!isReauthRequired(e)) throw e
    await requestReauth()
    return await fn()
  }
}

// lib/api.ts announces 'auth:expired' on EVERY 401 — including `reauth_required`, which would
// log the merchant out. Until that is fixed in core, verify before letting the event through:
// hold it, ask /api/auth/me, and only re-announce when the session is really gone.
let guardInstalled = false
const VERIFIED = Symbol('verified')
export function installAuthExpiryGuard() {
  if (guardInstalled || typeof window === 'undefined') return
  guardInstalled = true
  let checking = false
  window.addEventListener('auth:expired', (e) => {
    if ((e as any)[VERIFIED]) return
    e.stopImmediatePropagation()
    if (checking) return
    checking = true
    const reannounce = () => { const ev = new CustomEvent('auth:expired'); (ev as any)[VERIFIED] = true; window.dispatchEvent(ev) }
    fetch('/api/auth/me', { headers: { 'X-Requested-With': 'fetch', Accept: 'application/json' }, credentials: 'same-origin' })
      .then(res => { if (res.status === 401) reannounce() })
      .catch(() => { /* offline: keep the session; the next call will tell */ })
      .finally(() => { checking = false })
  }, true)
}

/** The dialog behind requestReauth()/withReauth(). Mount once (shells do). */
export function ReauthHost() {
  const [req, setReq] = useState<Request | null>(null)
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const reqRef = useRef<Request | null>(null)

  useEffect(() => {
    installAuthExpiryGuard()
    const mine = (r: Request) => { reqRef.current = r; setReq(r); setMode('password'); setSecret(''); setError(null); setCodeSentTo(null) }
    open = mine
    return () => {
      if (open === mine) open = null
      // Unmounted with the dialog up (layout switch, session expiry → /login): settle the request,
      // or every later withReauth() would wait forever on the shared in-flight promise.
      const r = reqRef.current
      reqRef.current = null
      if (r) { inflight = null; r.reject(cancelled()) }
    }
  }, [])

  const close = (ok: boolean) => {
    const r = reqRef.current
    if (!r) return
    reqRef.current = null
    if (ok) r.resolve()
    else r.reject(cancelled())
    setReq(null)
  }
  const submit = async () => {
    if (!secret.trim() || busy) return
    setBusy(true); setError(null)
    try {
      await api.post('/api/auth/reauth', mode === 'password' ? { password: secret } : { code: secret.replace(/\s/g, '') })
      close(true)
    } catch (e) {
      setError(e instanceof ApiError && e.status === 401 ? (mode === 'password' ? 'That password isn’t right.' : 'That code isn’t right or has expired.') : errorMessage(e))
      requestAnimationFrame(() => inputRef.current?.select())
    } finally { setBusy(false) }
  }
  const sendCode = async () => {
    setSending(true); setError(null)
    try {
      const r = await api.post<{ email?: string; sentTo?: string }>('/api/auth/reauth/code', {})
      setCodeSentTo((r && typeof r === 'object' && (r.sentTo || r.email)) || 'your email')
      setMode('code'); setSecret('')
      requestAnimationFrame(() => inputRef.current?.focus())
    } catch (e) { setError(errorMessage(e)) }
    finally { setSending(false) }
  }

  return (
    <Dialog
      open={!!req}
      onOpenChange={o => { if (!o && !busy) close(false) }}
      size="sm"
      title="Confirm it’s you"
      description={mode === 'password' ? 'For your security, enter your password to continue.' : <>We sent a 6-digit code to <span className="font-medium text-text">{codeSentTo}</span>.</>}
      footer={<>
        <Button onClick={() => close(false)} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void submit()} loading={busy} disabled={!secret.trim()}>Confirm</Button>
      </>}
    >
      <form onSubmit={e => { e.preventDefault(); void submit() }} className="flex flex-col gap-3">
        <Field label={mode === 'password' ? 'Password' : 'Code'} error={error ?? undefined}>
          {mode === 'password'
            ? <Input ref={inputRef} type="password" autoComplete="current-password" autoFocus value={secret} onChange={e => setSecret(e.target.value)} prefix={<KeyRound />} />
            : <Input ref={inputRef} inputMode="numeric" autoComplete="one-time-code" maxLength={8} autoFocus value={secret} onChange={e => setSecret(e.target.value)} prefix={<Mail />} className="tracking-[0.3em] tabular" placeholder="123456" />}
        </Field>
        <div className="flex justify-start">
          {mode === 'password'
            ? <Button variant="link" size="sm" onClick={() => void sendCode()} loading={sending}>Email me a code instead</Button>
            : <Button variant="link" size="sm" onClick={() => { setMode('password'); setSecret(''); setError(null) }}>Use my password instead</Button>}
        </div>
      </form>
    </Dialog>
  )
}
